import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertCircle, DollarSign, FileText } from 'lucide-react';
import financeService from '@/services/financeService';

const APARSummaryCards = ({ onViewDetails }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch receivables with payment history for accurate data
      const receivablesRes = await financeService.getReceivables({});
      let receivablesData = {
        count: 0,
        total_amount: 0,
        received_amount: 0,
        outstanding_amount: 0,
        overdue_count: 0
      };
      
      if (receivablesRes?.data?.success) {
        const receivablesList = receivablesRes.data.data.receivables || [];
        
        // Fetch payment history for each receivable
        const receivablesWithPayments = await Promise.all(
          receivablesList.map(async (receivable) => {
            try {
              const paymentsResponse = await financeService.getReceivablePaymentHistory(receivable.id);
              const payments = paymentsResponse.data.success ? paymentsResponse.data.data.payments || [] : [];
              return { ...receivable, Payments: payments };
            } catch (error) {
              return { ...receivable, Payments: [] };
            }
          })
        );
        
        // Calculate totals
        const totalAmount = receivablesWithPayments.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
        const receivedAmount = receivablesWithPayments.reduce((sum, r) => {
          if (r.Payments && Array.isArray(r.Payments)) {
            const paymentsTotal = r.Payments.reduce((paySum, payment) => paySum + parseFloat(payment.amount || 0), 0);
            return sum + paymentsTotal;
          }
          return sum;
        }, 0);
        const outstandingAmount = totalAmount - receivedAmount;
        
        receivablesData = {
          count: receivablesWithPayments.length,
          total_amount: totalAmount,
          received_amount: receivedAmount,
          outstanding_amount: outstandingAmount,
          overdue_count: 0 // Can be calculated based on aging if needed
        };
      }
      
      // Fetch payables summary (keep as is)
      const payablesRes = await financeService.getAPARSummary();
      let payablesData = {
        count: 0,
        total_amount: 0,
        paid_amount: 0,
        outstanding_amount: 0,
        overdue_count: 0
      };
      
      if (payablesRes?.data?.success && payablesRes.data.data.payables) {
        payablesData = payablesRes.data.data.payables;
      }
      
      // Calculate net position
      const netPosition = receivablesData.received_amount - payablesData.outstanding_amount;
      
      setSummary({
        receivables: receivablesData,
        payables: payablesData,
        net_position: netPosition
      });
    } catch (err) {
      console.error('Error fetching AP/AR summary:', err);
      setError('Failed to load AP/AR summary');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return '₦' + new Intl.NumberFormat('en-NG', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded w-2/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded w-2/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
        <div className="flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const { payables, receivables, net_position } = summary;
  const netPositionValue = parseFloat(net_position || 0);
  const isPositive = netPositionValue >= 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Payables Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 overflow-hidden group">
        <div className="bg-gradient-to-r from-red-500 to-red-600 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="bg-white/20 p-2 rounded-lg">
                <TrendingDown className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white">Accounts Payable</h3>
            </div>
            <FileText className="h-6 w-6 text-white/60" />
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {/* Total Amount */}
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Amount</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(payables?.total_amount)}
              </p>
            </div>

            {/* Outstanding Balance */}
            <div className="flex items-center justify-between bg-red-50 rounded-lg p-3">
              <div>
                <p className="text-sm text-red-600 font-medium mb-1">Outstanding</p>
                <p className="text-xl font-bold text-red-700">
                  {formatCurrency(payables?.outstanding_amount)}
                </p>
              </div>
              {payables?.overdue_count > 0 && (
                <div className="flex items-center space-x-1 bg-red-100 px-3 py-1.5 rounded-full">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-bold text-red-700">
                    {payables.overdue_count} overdue
                  </span>
                </div>
              )}
            </div>

            {/* Paid Amount */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Paid Amount</span>
              <span className="font-semibold text-green-600">
                {formatCurrency(payables?.paid_amount)}
              </span>
            </div>

            {/* Count */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Total Payables</span>
              <span className="font-semibold text-gray-900">{payables?.count || 0}</span>
            </div>
          </div>

          <button
            onClick={() => onViewDetails('payables')}
            className="w-full mt-4 bg-red-600 text-white py-2.5 rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            View Details
          </button>
        </div>
      </div>

      {/* Receivables Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 overflow-hidden group">
        <div className="bg-gradient-to-r from-green-500 to-green-600 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="bg-white/20 p-2 rounded-lg">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white">Accounts Receivable</h3>
            </div>
            <DollarSign className="h-6 w-6 text-white/60" />
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {/* Received Amount - Main Display */}
            <div>
              <p className="text-sm text-gray-600 mb-1">Received Amount</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(receivables?.received_amount)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Actual income received</p>
            </div>

            {/* Outstanding Balance */}
            <div className="flex items-center justify-between bg-orange-50 rounded-lg p-3 border border-orange-200">
              <div>
                <p className="text-sm text-orange-600 font-medium mb-1">Outstanding</p>
                <p className="text-xl font-bold text-orange-700">
                  {formatCurrency(receivables?.outstanding_amount)}
                </p>
              </div>
              {receivables?.overdue_count > 0 && (
                <div className="flex items-center space-x-1 bg-orange-100 px-3 py-1.5 rounded-full">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  <span className="text-sm font-bold text-orange-700">
                    {receivables.overdue_count} overdue
                  </span>
                </div>
              )}
            </div>

            {/* Total Invoiced */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Total Invoiced</span>
              <span className="font-semibold text-gray-900">
                {formatCurrency(receivables?.total_amount)}
              </span>
            </div>

            {/* Count */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Total Receivables</span>
              <span className="font-semibold text-gray-900">{receivables?.count || 0}</span>
            </div>
          </div>

          <button
            onClick={() => onViewDetails('receivables')}
            className="w-full mt-4 bg-green-600 text-white py-2.5 rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            View Details
          </button>
        </div>
      </div>

      {/* Net Position Banner */}
      <div className="md:col-span-2">
        <div className={`rounded-xl p-4 ${isPositive ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200' : 'bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${isPositive ? 'bg-green-100' : 'bg-red-100'}`}>
                {isPositive ? (
                  <TrendingUp className={`h-6 w-6 ${isPositive ? 'text-green-600' : 'text-red-600'}`} />
                ) : (
                  <TrendingDown className={`h-6 w-6 ${isPositive ? 'text-green-600' : 'text-red-600'}`} />
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Net Position</p>
                <p className="text-xs text-gray-500">(Receivables - Payables)</p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-3xl font-bold ${isPositive ? 'text-green-700' : 'text-red-700'}`}>
                {formatCurrency(Math.abs(netPositionValue))}
              </p>
              <p className={`text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {isPositive ? 'Positive' : 'Negative'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APARSummaryCards;
